import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Home, Building2, Landmark, CheckCircle, XCircle, Phone } from 'lucide-react';
import axios from 'axios';
import { submitLead } from '../../services/hubspot';
import PrivacyCheckbox from '../../components/PrivacyCheckbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const categories = [
  {
    id: 'wohnungen',
    title: 'Wohnungen',
    icon: Home,
    description: 'Eigentumswohnungen, Mietwohnungen, Penthäuser und Apartments im gesamten Rhein-Main-Gebiet.',
    features: ['Eigentumswohnungen', 'Mietwohnungen', 'Penthäuser', 'Apartments', 'Maisonetten']
  },
  {
    id: 'liegenschaften',
    title: 'Liegenschaften',
    icon: Landmark,
    description: 'Mehrfamilienhäuser, Wohn- und Geschäftshäuser sowie Anlageimmobilien für Investoren.',
    features: ['Mehrfamilienhäuser', 'Wohn-/Geschäftshäuser', 'Anlageimmobilien', 'Portfolios']
  },
  {
    id: 'gewerbe',
    title: 'Gewerbe',
    icon: Building2,
    description: 'Büroflächen, Einzelhandel, Lagerhallen und Produktionsstätten für Unternehmen.',
    features: ['Büroflächen', 'Einzelhandelsflächen', 'Lagerhallen', 'Produktionsstätten', 'Praxisräume']
  }
];

export default function Vermittlung() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) return;
    
    setLoading(true);
    setStatus(null);

    const hubspotResult = await submitLead('vermittlung', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      propertyType: formData.property_type,
      message: formData.message
    });

    try {
      await axios.post(`${API}/api/lead`, { ...formData, service_type: 'Vermittlung' });
    } catch (error) {
      console.error('Backend submission error:', error);
    }

    setLoading(false);
    
    if (hubspotResult.success) {
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', property_type: '', message: '' });
      setPrivacyAccepted(false);
    } else {
      setStatus('error');
    }
  };

  const StatusMessage = () => (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`p-4 mb-6 flex items-start gap-3 ${
            status === 'success' 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}
        >
          {status === 'success' ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">Vielen Dank!</p>
                <p className="text-green-400/80 text-sm">Serdar Dogan meldet sich in Kürze bei Ihnen.</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Es ist ein Fehler aufgetreten.</p>
                <p className="text-red-400/80 text-sm">
                  Bitte rufen Sie uns direkt an: 
                  <a href="tel:+491756900374" className="ml-1 inline-flex items-center gap-1 text-[#d1af6a] hover:underline">
                    <Phone className="w-3 h-3" /> +49 175 6900374
                  </a>
                </p>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div data-testid="vermittlung-page">
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <Link to="/leistungen" className="text-[#c8c6c4] hover:text-[#d1af6a]">Leistungen</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Vermittlung</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">VERMITTLUNG</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">Immobilienvermittlung</h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Ob Wohnung, Liegenschaft oder Gewerbeobjekt – ich vermittle Immobilien aller Art und finde das passende Objekt für Ihre Anforderungen.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="wohnungen" className="w-full">
            <TabsList className="w-full justify-start bg-transparent border-b border-[#d1af6a]/20 rounded-none h-auto p-0 mb-12">
              {categories.map((cat) => (
                <TabsTrigger key={cat.id} value={cat.id} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#d1af6a] data-[state=active]:bg-transparent data-[state=active]:text-[#d1af6a] text-[#c8c6c4] px-6 py-4">
                  <cat.icon className="w-5 h-5 mr-2" />
                  {cat.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {categories.map((cat) => (
              <TabsContent key={cat.id} value={cat.id}>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">{cat.title}</h2>
                    <p className="text-[#c8c6c4] leading-relaxed mb-8">{cat.description}</p>
                    <ul className="space-y-3">
                      {cat.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-3 text-white">
                          <div className="w-2 h-2 bg-[#d1af6a]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="aspect-[4/3] bg-[#1a2640] border border-[#d1af6a]/20 flex items-center justify-center">
                    <cat.icon className="w-32 h-32 text-[#d1af6a]/20" />
                  </div>
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      <section className="py-24 bg-[#1a2640]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">Suchauftrag stellen</h2>
            <p className="text-[#c8c6c4]">Beschreiben Sie mir, wonach Sie suchen.</p>
          </div>
          
          <StatusMessage />
          
          {status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="vermittlung-form">
              <input type="text" placeholder="Ihr Name *" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="input-luxury w-full" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="email" placeholder="E-Mail *" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="input-luxury w-full" />
                <input type="tel" placeholder="Telefon *" required value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="input-luxury w-full" />
              </div>
              <Select value={formData.property_type} onValueChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}>
                <SelectTrigger className="input-luxury w-full h-auto"><SelectValue placeholder="Ich suche..." /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Wohnung">Wohnung</SelectItem>
                  <SelectItem value="Haus">Haus</SelectItem>
                  <SelectItem value="Mehrfamilienhaus">Mehrfamilienhaus</SelectItem>
                  <SelectItem value="Gewerbe">Gewerbefläche</SelectItem>
                  <SelectItem value="Grundstück">Grundstück</SelectItem>
                </SelectContent>
              </Select>
              <textarea placeholder="Beschreiben Sie Ihre Anforderungen: Lage, Größe, Budget, Besonderheiten..." rows={5} value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} className="input-luxury w-full resize-none" />
              <PrivacyCheckbox checked={privacyAccepted} onCheckedChange={setPrivacyAccepted} />
              <button type="submit" disabled={loading || !privacyAccepted} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Wird gesendet…' : 'Suchauftrag senden'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
